import { Application, Controller } from '@hotwired/stimulus';
import { ValueDefinitionMap } from '@hotwired/stimulus/dist/types/core/value_properties';
import { bindDataToTemplate, toRegexAST, validateByRegexAst } from '../../500-common/util';
import { KeypadController } from '../keypad/keypad.stimulus';
import { initTooltips } from '../tooltips/tooltips';

/**
 * Configuration of form-field conditions
 */
type When = Record<string, WhenRule>;

type WhenRule = {
    value: string;
    attr: 'hidden' | 'disabled' | 'readonly';
    default: boolean;
};

/**
 * This is the WIP stimulus rewrite of the form field client-side functionality.
 * This will be a frankenstein migration as the size of the client-side functionality
 * is quite big. Therefore, you find most of the functionality still in form-field.ts
 * and some migrated or newly added functionality in this stimulus controller.
 * Sorry for that, mate, but sometimes we have to start somewhere.
 * @see form-field.ts
 */
export class FormFieldsController extends Controller<HTMLFormElement> {
    static readonly INVALID_FIELD_TARGET = 'invalid';
    static readonly INVALID_FIELD_MESSAGE_ATTRIBUTE = 'data-error';
    static readonly INVALID_FIELD_MESSAGE_MEDIUM_ATTRIBUTE = 'data-error-medium';
    static readonly MASKED_FIELD_GROUP_PATTERN_ATTRIBUTE = 'data-pattern';

    static values: ValueDefinitionMap = {
        invalid: {
            type: Boolean,
            default: false,
        },
        disabledWhenInvalid: {
            type: Boolean,
            default: false,
        },
        rowSelector: {
            type: String,
            default: '.klt-c-form-row',
        },
    };

    static classes = ['fieldError'];
    static targets = [
        'errorMessageTemplate',
        'errorMessageTooltipTemplate',
        'errorTooltipMessageSource',
        'errorMessageSource',
        'fieldWrapper',
        'input',
        'maxlengthCounter',
        'maskedField',
        'maskedFieldGroup',
        this.INVALID_FIELD_TARGET,
    ];

    declare readonly hasNumberTarget: boolean;
    declare readonly hasFieldErrorClass: boolean;
    declare readonly fieldErrorClasses: string[];
    declare readonly fieldErrorClass: string;
    declare readonly errorMessageTemplateTarget: HTMLTemplateElement;
    declare readonly errorMessageTooltipTemplateTarget: HTMLTemplateElement;
    declare readonly hasErrorMessageTooltipTemplateTarget: boolean;
    declare readonly hasErrorMessageTemplateTarget: boolean;
    declare readonly maskedFieldTargets: HTMLInputElement[];
    declare readonly fieldWrapperTargets: HTMLElement[];
    declare readonly disabledWhenInvalidValue: boolean;
    declare readonly rowSelectorValue: string;

    /**
     * Returns all HTML input, textarea, radio, checkbox etc. elements.
     * Except it is a masked field.
     */
    declare readonly inputTargets: HTMLInputElement[];

    declare readonly inputTarget: HTMLInputElement;
    declare readonly hasInputTarget: boolean;

    /**
     * An element holding the current character count for a field with a maxlength
     */
    declare readonly maxlengthCounterTargets: HTMLElement[];

    /**
     * A field that has an active error rendered as tooltip
     */
    declare readonly errorMessageTooltipSource: HTMLInputElement;

    /**
     * A field that has an active error rendered as inline text
     */
    declare readonly errorMessageSource: HTMLInputElement;

    /**
     * Name of the component to be used in HTML e.g. data-controller="form-fields"
     */
    static readonly componentName = 'form-fields';

    /**
     * Context aware listener for maskedInputKeydown events.
     * Used to be able removing the event listener in a
     * disconnected hook and at the same time, having 'this' access
     * to the current controller instance
     */
    maskedInputKeydownListener = (e) => this.onMaskedFieldGroupKeyPress(e);

    /**
     * Context aware listener for maskedInput Paste events.
     * Used to be able removing the event listener in a
     * disconnected hook and at the same time, having 'this' access
     * to the current controller instance
     */
    maskedInputPasteListener = (e) => this.onMaskedFieldGroupPaste(e);

    /**
     * Get a clone of markup for rendering an error message tooltip
     */
    get errorMessageTooltipHTMLElement(): HTMLElement {
        if (this.hasErrorMessageTooltipTemplateTarget) {
            return this.errorMessageTooltipTemplateTarget.content.firstElementChild.cloneNode(true) as HTMLElement;
        }
    }

    /**
     * Get a clone of markup for rendering a error message
     */
    get errorMessageHTMLElement(): HTMLElement {
        if (this.hasErrorMessageTemplateTarget) {
            return this.errorMessageTemplateTarget.content.firstElementChild.cloneNode(true) as HTMLElement;
        }
    }

    connect() {
        this.initConditionalFields();
        this.initMaxlengthCounter();

        this.element.addEventListener('input', (event) => this.onInput(event));
        this.element.addEventListener('focus', (event) => this.onFocus(event), true);
        this.element.addEventListener('submit', (event) => this.onSubmit(event), true);

        this.validate();
    }

    /**
     * Adds a maxlength counter to the form field if has the maxlength attribute on it
     */
    initMaxlengthCounter() {
        const maxlengthInputs = this.inputTargets.filter((input) => FormFieldsController.shouldHaveCounter(input));

        maxlengthInputs.forEach((input) => {
            const counterElement = document.createElement('span');

            // Not necessary to expose to screenreaders as maxlength is already marked with attribute
            counterElement.setAttribute('aria-hidden', 'true');
            counterElement.setAttribute('data-form-fields-target', 'maxlengthCounter');
            counterElement.setAttribute('data-input-id', input.id);

            input.parentElement.append(counterElement);
        });
    }

    /**
     * Read the data-when="..." values from all fields and initalized the necessary actions/events based on the rules given
     */
    initConditionalFields() {
        this.fieldWrapperTargets
            .filter((wrapper) => wrapper.dataset.when)
            .forEach((conditionalField) => {
                try {
                    const conditions = this.parseWhen(conditionalField.dataset.when);

                    Object.entries(conditions).forEach(([sourceId, rule]) => {
                        const sourceElement = this.inputTargets.find((input) => input.id === sourceId);

                        if (sourceElement) {
                            sourceElement.setAttribute(
                                'aria-controls',
                                `${sourceElement.getAttribute('aria-controls') || ''} ${conditionalField.id}`.trim()
                            );

                            sourceElement.setAttribute(
                                `data-action`,
                                `${FormFieldsController.componentName}#onChangeWithConditions`
                            );
                        }

                        this.setConditionalFieldState(rule, conditionalField);
                    });
                } catch (e) {}
            });
    }

    /**
     * Conditional Fields Update. Is triggered by data-action="form-fields#onChangeWithConditions" and is emant to be used
     * on fields whose change have an effect on other fields.
     */
    onChangeWithConditions(event: Event) {
        const controllerInput = event.currentTarget as HTMLInputElement;
        const controlledElementsSpec = controllerInput.getAttribute('aria-controls');
        if (controlledElementsSpec) {
            controlledElementsSpec
                .split(' ')
                .map((elementId) => this.fieldWrapperTargets.find((field) => field.id === elementId))
                .forEach((conditionalField) => {
                    const when = this.parseWhen(conditionalField.dataset.when)[controllerInput.id];

                    if (controllerInput instanceof HTMLSelectElement) {
                        const currentValue = controllerInput.options[controllerInput.selectedIndex].value;

                        // When value can contain a comma-separated value list.
                        const matchList = when.value.split(',').map((condition) => condition.trim());

                        this.setConditionalFieldState(when, conditionalField, matchList.includes(currentValue));
                    }
                });
        }
    }

    /**
     * @param rule Ruleset
     * @param field The conditional field to apply it to
     * @param active If true, the field is shown. If not set, the current state will be taken from the DOM
     */
    setConditionalFieldState(rule: WhenRule, field: HTMLElement, active?: boolean) {
        const inputElements = field.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
            'input, select, textarea'
        );

        if (active === undefined) {
            const controllerInput = this.element.querySelector(`[aria-controls*='${field.id}']`);

            if (controllerInput) {
                // When value can contain a comma-separated value list.
                const matchList = rule.value.split(',').map((condition) => condition.trim());

                if (controllerInput instanceof HTMLSelectElement) {
                    const currentValue = controllerInput.options[controllerInput.selectedIndex].value;
                    active = matchList.includes(currentValue);
                } else if (controllerInput instanceof HTMLInputElement) {
                    if (['checkbox', 'radio'].includes(controllerInput.type)) {
                        console.info(`klt/form-fields: Handlers for checkbox and radio conditionals not implemented`);
                    } else {
                        active = matchList.includes(controllerInput.value);
                    }
                }
            } else {
                console.error(`klt/form-fields: No element with [aria-controls*="${field.id}"] found`);
                return;
            }
        }

        const formRow = field.closest(this.rowSelectorValue) as HTMLElement;

        switch (rule.attr) {
            case 'hidden':
                field.hidden = !active;
                // Also disable inputs in addition to hiding the whole field wrapper to ensure
                // the value is not sent to the server on submit
                inputElements.forEach((element) =>
                    field.hidden ? element.setAttribute('disabled', '') : element.removeAttribute('disabled')
                );

                // If element is part of a form row, toggle the whole form row.
                if (formRow) {
                    formRow.hidden = field.hidden;
                }

                break;
            case 'disabled':
                inputElements.forEach((element) => (element.disabled = !active));

                break;
            case 'readonly':
                inputElements.forEach((element) => {
                    if (element instanceof HTMLInputElement) {
                        element.readOnly = !active;
                    }
                });

                break;
        }
    }

    /**
     * Parses the value of a single when condition
     */
    parseWhen(whenValueString: string): When {
        const conditions: Record<string, When | string> = JSON.parse(whenValueString);

        const defaultWhen: WhenRule = {
            value: '',
            attr: 'hidden',
            default: true,
        };

        const when = Object.entries(conditions).reduce<When>((acc, [sourceId, ruleObjOrString]): When => {
            if (typeof ruleObjOrString === 'string') {
                return {
                    ...acc,
                    [sourceId]: {
                        ...defaultWhen,
                        value: ruleObjOrString,
                    },
                };
            } else {
                return {
                    ...acc,
                    [sourceId]: {
                        ...defaultWhen,
                        ...ruleObjOrString,
                    },
                };
            }
        }, {});

        return when;
    }

    /**
     * Initalize event handlers for a masked field group
     * @example <div data-form-fields-target="maskedFieldGroup"></div>
     * @param element The element having [data-form-fields-target="maskedFieldGroup"]
     */
    maskedFieldGroupTargetConnected(element: HTMLElement) {
        const fields = element.querySelectorAll('input');

        if (fields.length) {
            // Group fields by giving all the same randomly generated id.
            // This is used when you used a masked input that is connected
            // by multiple fields.
            const group = Math.floor(Math.random() * 9999).toString();
            fields.forEach((field) => {
                field.setAttribute(`data-${FormFieldsController.componentName}-group-param`, group);
                field.setAttribute(`data-${FormFieldsController.componentName}-target`, 'maskedField');
                this.maskedFieldTargetConnected(field);
            });

            KeypadController.addAsOuputElement(fields[0]);
        } else {
            console.error('Form Fields Controller: No input found for the masked input group target.');
            return;
        }

        element.addEventListener('keydown', this.maskedInputKeydownListener);
        element.addEventListener('paste', this.maskedInputPasteListener);
    }

    maskedFieldGroupTargetDisconnected(element: HTMLElement) {
        element.removeEventListener('keydown', this.maskedInputKeydownListener);
        element.removeEventListener('paste', this.maskedInputPasteListener);
    }

    /**
     * Initalize event handlers for masked fields
     * @param element The element having [data-form-fields-target="maskedField"]
     */
    maskedFieldTargetConnected(field: HTMLInputElement) {
        if (!field.pattern) {
            console.warn('Form fields controller: Field does not have a pattern', field);
        }

        const actionValue = `${FormFieldsController.componentName}#maskedFieldInput`;
        if (field.hasAttribute('data-action') && !field.getAttribute('data-action').includes(actionValue)) {
            field.setAttribute('data-action', `${field.getAttribute('data-action')} ${actionValue}`);
        } else if (!field.hasAttribute('data-action')) {
            field.setAttribute('data-action', `${actionValue}`);
        }

        field.setAttribute('autocomplete', 'off');

        // Remove the HTML placeholder attribute and replace it with
        // a fake placeholder that dynamically changes with the user input.
        // e.g. ____-____ becomes xx__-____ (where the 'x' represents whitespace)
        if (field.placeholder) {
            const placeholder = field.placeholder;
            field.setAttribute(`data-${FormFieldsController.componentName}-placeholder-param`, placeholder);

            const dynamicPlaceholder = document.createElement('div');
            dynamicPlaceholder.setAttribute('aria-hidden', 'true');
            dynamicPlaceholder.setAttribute('data-masked-field-placeholder', '');
            dynamicPlaceholder.innerText = placeholder;

            field.removeAttribute('placeholder');
            field.after(dynamicPlaceholder);

            if (!field.hasAttribute('maxlength')) {
                field.setAttribute('maxlength', placeholder.length.toString());
            }
        }
    }

    /**
     * Initalize event handlers for [data-form-fields-target="maxlengthCounter"]
     * This is also implizitly added by the initMaxlengthCounter function
     */
    maxlengthCounterTargetConnected(counterElement: HTMLSpanElement) {
        const connectedInputId = counterElement.getAttribute('data-input-id');
        const connectedInput = this.element.querySelector<HTMLInputElement | HTMLTextAreaElement>(
            `#${connectedInputId}`
        );

        if (connectedInput) {
            this.updateCharacterCounter(counterElement, connectedInput);
        }
    }

    /**
     * Handles input changed on a masked field like automatically applying the given separaters
     */
    maskedFieldInput(e: Event & { params?: { separator?: string; placeholder?: string; group?: string } }) {
        let field = (e.currentTarget || e.target) as HTMLInputElement;

        // Custom event means, this function is dispatched by another Stimulus controller e.g. by the Keypad.
        // SO we need the manually assigned target then.
        if (e instanceof CustomEvent) {
            field = e.target as HTMLInputElement;
        }

        const pattern = field.pattern;
        const separator =
            e.params?.separator || field.getAttribute(`data-${FormFieldsController.componentName}-separator-param`);
        const placeholder = e.params?.placeholder || field.placeholder;

        /**
         * Indicates the field belongs to a group of fields and is not just a single, masked field
         */
        const group = e.params?.group || field.getAttribute(`data-${FormFieldsController.componentName}-group-param`);

        /**
         * Virtual placeholder element used in case of a single, masked field
         */
        const placeholderElement = field.parentElement.querySelector<HTMLElement>('[data-masked-field-placeholder]');

        /**
         * Indicates when the user removed a character instead of added one
         */
        const characterRemoved = FormFieldsController.getPreviousFieldValue(field)?.length > field.value.length;

        /**
         * Regex to split string by its separator
         */
        const separatorSplitRegex = separator ? new RegExp(`${separator}`, 'g') : null;

        /**
         * The input groups of the placeholder value, used to detect the regular expression for each group
         */
        const placeholderInputGroups = separator && placeholder ? placeholder.split(separator) : null;

        /**
         * The value given by the user, split into all its groups
         */
        let typedInputGroups: string[] = null;

        /**
         * is true, when the input  is accepted
         */
        let validUserInput: boolean = false;

        if (!pattern || characterRemoved) {
            // In a backspace scenario, we don't want to validate anything and we also
            // don't want to trigger any event handlers that might change the position of
            // the cursor
            FormFieldsController.rememberCurrentFieldValue(field);
            validUserInput = true;
        }

        /**
         * Very simplified AST of the regex given in the pattern
         */
        const regexAst = pattern ? toRegexAST(pattern) : null;

        if (group) {
            const groupFields = this.getFieldsOfGroup(group);

            typedInputGroups = groupFields.map((groupField) => groupField.value);

            const indexOfCurrentFieldInGroup = this.getIndexOfFieldInGroup(groupFields, field);
            const isLastInputInGroup = indexOfCurrentFieldInGroup === groupFields.length - 1;
            const regexForGroup = new RegExp(pattern);
            const groupValidAndFinished = regexForGroup.exec(field.value);

            if (regexAst && !characterRemoved) {
                validUserInput = validateByRegexAst(regexAst, field.value, 'characterClass');
            }

            if (validUserInput) {
                if (groupValidAndFinished && !isLastInputInGroup) {
                    const nextField = groupFields[indexOfCurrentFieldInGroup + 1];
                    if (KeypadController.isKeypadConnected(this.element)) {
                        KeypadController.removeAsOutputElement(field);
                        KeypadController.addAsOuputElement(nextField);
                    }

                    if (e.type !== 'keypad:keypress') {
                        nextField.focus();
                    }
                }

                FormFieldsController.rememberCurrentFieldValue(field);
            } else {
                FormFieldsController.restorePreviousFieldValue(field);
            }
        } else if (separator && placeholder && regexAst) {
            // If placeholder is given, we use this for comparison to the current user value to be able
            // to automatically add separators in the right moment.
            // We also individually check each input group for validity. For that, we split the regex given via
            // the HTML pattern property in individual groups (based on regex capturing groups).
            // This will currently only work with a regex format like "([allowed characters])-([allowed characters])-..."
            typedInputGroups = field.value.split(separator);

            if (!characterRemoved) {
                /**
                 * Is true when all characters in the previous group are filled according to the placeholder.
                 * This is a necessary check to ensure the user doesn't input a separator too early, which
                 * the regex check will accept as a valid value.
                 */
                const validFormat =
                    typedInputGroups.length > 1
                        ? typedInputGroups[typedInputGroups.length - 2]?.length ===
                          placeholderInputGroups[typedInputGroups.length - 2]?.length
                        : true;

                if (!validFormat) {
                    FormFieldsController.restorePreviousFieldValue(field);
                    return;
                }

                /**
                 * The pattern given on the input, split in its separate capturing groups
                 * to allow to validate each input group separately.
                 */
                const regexInputGroups = regexAst.expressions.filter((regExpression) => regExpression.type === 'group');

                // Check how many characters the user already typed and find the appropriate group.
                // The index of the found group then can be used to check with the correct regex group
                // or for example, to update the placeholder.
                let i = 0;
                const groupIndex = placeholderInputGroups.findIndex((currentGroup) => {
                    i += currentGroup.length;
                    const currentInputValueLength = field.value.replace(separatorSplitRegex, '').length;
                    return currentInputValueLength <= i;
                });

                const regexForGroup = new RegExp(regexInputGroups[groupIndex].value);
                const groupValidAndFinished = regexForGroup.exec(typedInputGroups[groupIndex]);

                validUserInput = validateByRegexAst(
                    regexInputGroups[groupIndex],
                    typedInputGroups[groupIndex],
                    'characterClass'
                );

                if (validUserInput) {
                    // Automatically add separators
                    if (groupValidAndFinished && placeholderInputGroups.length - 1 !== groupIndex) {
                        field.value = `${field.value}${separator}`;
                    }

                    FormFieldsController.rememberCurrentFieldValue(field);
                } else {
                    FormFieldsController.restorePreviousFieldValue(field);
                }
            }
        }

        // Update the virtual placeholder
        if (validUserInput && placeholder && separator && placeholderElement) {
            const newPlaceholderPreviewValue = placeholderInputGroups.reduce(
                (replacedPlaceholderInputGroups, group, i) => {
                    const typedGroup = typedInputGroups[i] || null;

                    if (typedGroup) {
                        const singlePlaceholderCharacter = group.substring(0, 1);
                        const charactersToReplaceWithPlaceholders = group.length - typedGroup.length;
                        const val = `${'&nbsp;'.repeat(typedGroup.length)}${singlePlaceholderCharacter.repeat(
                            charactersToReplaceWithPlaceholders
                        )}`;

                        replacedPlaceholderInputGroups[i] = val;
                    }

                    return replacedPlaceholderInputGroups;
                },
                placeholderInputGroups
            );

            placeholderElement.innerHTML = newPlaceholderPreviewValue.join(separator);
        }

        if (validUserInput) {
            this.dispatch('inputValid');
        }
    }

    /**
     * Handler for masked field keypresses
     */
    onMaskedFieldGroupKeyPress({ key, target }: KeyboardEvent) {
        // If the user types a backspace in a masked input group, but the focused field
        // is already empty, delete the last character of the previous field and focus it.
        // That's the same way the group would work when it only would be one big input field.
        if (key === 'Backspace' && target instanceof HTMLInputElement && !target.value.length) {
            const group = target.getAttribute(`data-${FormFieldsController.componentName}-group-param`);

            if (group) {
                const groupFields = this.getFieldsOfGroup(group);
                const indexOfCurrentFieldInGroup = this.getIndexOfFieldInGroup(groupFields, target);
                const isFirstInputInGroup = indexOfCurrentFieldInGroup === 0;

                if (!isFirstInputInGroup) {
                    const previousFieldInGroup = groupFields[indexOfCurrentFieldInGroup - 1];
                    previousFieldInGroup.value = previousFieldInGroup.value
                        ? previousFieldInGroup.value.slice(0, -1)
                        : previousFieldInGroup.value;
                    previousFieldInGroup.focus();
                }
            }
        }
    }

    /**
     * Handler for paste events in a field group
     */
    onMaskedFieldGroupPaste(e: ClipboardEvent) {
        const pastedText = e.clipboardData.getData('text/plain');
        if (pastedText && e.target instanceof HTMLInputElement && e.clipboardData && e.clipboardData.getData) {
            const group = e.target.getAttribute(`data-${FormFieldsController.componentName}-group-param`);

            if (group) {
                e.preventDefault();
                const groupFields = this.getFieldsOfGroup(group);

                // Check that all fields have a maxlength info so we can correctly split the pasted text
                if (
                    groupFields.filter((field) => field.maxLength && field.maxLength > -1).length === groupFields.length
                ) {
                    groupFields.forEach((field, i) => {
                        const maxLength = field.maxLength;
                        const charsFrom = i * maxLength;
                        const charsTo = charsFrom + maxLength;
                        const valueForGroup = pastedText.slice(charsFrom, charsTo);
                        field.value = valueForGroup;
                    });

                    groupFields[groupFields.length - 1].focus();
                }
            }
        }
    }

    /**
     * Event Delegation handler for input events inside a form
     */
    onInput(event: Event) {
        const target = event.target as HTMLInputElement;

        if (target.getAttribute('type') === 'number') {
            FormFieldsController.handleNumberFieldChanges(target);
        }

        if (FormFieldsController.shouldHaveCounter(target)) {
            const counterElement = this.maxlengthCounterTargets.find(
                (counterElement) => counterElement.getAttribute('data-input-id') === target.id
            );

            this.updateCharacterCounter(counterElement, target);
        }

        this.validate();
    }

    /**
     * Event Delegation handler for focus events inside a form
     */
    onFocus(event: Event) {
        const target = event.target as HTMLInputElement;

        if ((target as HTMLInputElement).getAttribute('type') === 'number') {
            const { min, max } = target;

            if (min || max) {
                FormFieldsController.rememberCurrentFieldValue(target);
            }
        }
    }

    onSubmit(event: Event) {
        const isValid = this.validate();

        if (!isValid) {
            event.preventDefault();
        }
    }

    /**
     * Event handler for change events in number fields.
     * Checks for certain attributes like min/max and ensures e.g. that
     * no higher or lower numbers can be entered.
     */
    static handleNumberFieldChanges(target: HTMLInputElement) {
        const { min, max, value, maxLength } = target;

        if (!('formFieldPreviousValue' in target.dataset)) {
            FormFieldsController.rememberCurrentFieldValue(target);
        }

        if ('formFieldPreviousValue' in target.dataset) {
            if (value === '') {
                FormFieldsController.restorePreviousFieldValue(target);
            }

            if (max || min || maxLength > -1) {
                if (max && parseInt(value) > parseInt(max)) {
                    FormFieldsController.restorePreviousFieldValue(target);
                }

                if (min && parseInt(value) < parseInt(min)) {
                    FormFieldsController.restorePreviousFieldValue(target);
                }

                if (maxLength !== -1 && value.length > maxLength) {
                    // Some mobile browsers allow to type in more characters and will only
                    // remove them after the field loses focus. That's why we enforce the
                    // same behaviour as on desktop here.
                    FormFieldsController.restorePreviousFieldValue(target);
                }
            }

            FormFieldsController.rememberCurrentFieldValue(target);
        }
    }

    /**
     * Stores the current value in a data attribute on the DOM element for later retrieval
     */
    static rememberCurrentFieldValue(field: HTMLInputElement) {
        field.dataset.formFieldPreviousValue = field.value;
    }

    /**
     * Restores the stored value from the data attribute of the DOM element as the field value
     */
    static restorePreviousFieldValue(field: HTMLInputElement) {
        field.value = FormFieldsController.getPreviousFieldValue(field) || '';
    }

    static getPreviousFieldValue(field: HTMLInputElement) {
        return field.dataset.formFieldPreviousValue;
    }

    /**
     * Called whenever a data-form-fields-target="errorMessageSource" is added to a form field input element
     */
    invalidTargetConnected(element: HTMLInputElement) {
        this.renderError(element);

        // No longer needed, assuming data-error was not rendered as error message
        element.removeAttribute(FormFieldsController.INVALID_FIELD_MESSAGE_ATTRIBUTE);
    }

    invalidTargetDisconnected(element: HTMLInputElement) {
        this.removeError(element);
    }

    /**
     * Allows to set a form field to invalid from outside of Stimulus by calling
     * FormFieldController.markInputInvalid without creating an instance.
     */
    static markInputInvalid(input: HTMLInputElement, message: string) {
        // Reset to trigger change detection
        this.markInputValid(input);

        input.setAttribute(this.INVALID_FIELD_MESSAGE_ATTRIBUTE, message);
        input.setAttribute('data-form-fields-target', this.INVALID_FIELD_TARGET);
    }

    /**
     * Remove error message from form field outside any Stimulus controller
     * by passing a valid input element that is rendered inside data-controller="form-fields" wrappers
     */
    static markInputValid(input: HTMLInputElement) {
        input.removeAttribute(this.INVALID_FIELD_MESSAGE_ATTRIBUTE);
        input.removeAttribute(`data-form-fields-target`);
    }

    /**
     * Register the stimulus controller on the application
     */
    static register(application: Application) {
        application.register(FormFieldsController.componentName, this);
    }

    static shouldHaveCounter(input: HTMLInputElement) {
        return input.maxLength > 0 && input.nodeName.toLowerCase() === 'textarea';
    }

    /**
     * Renders the data-error on the given 'input' into an error message.
     * Detects data-error-medium, can be 'inline' or 'tooltip', defaults to 'inline'.
     * See also FormFieldsController.markInputInvalid()
     */
    private renderError(input: HTMLInputElement) {
        const errorMessage = input.getAttribute(FormFieldsController.INVALID_FIELD_MESSAGE_ATTRIBUTE);
        const errorMedium = input.getAttribute(FormFieldsController.INVALID_FIELD_MESSAGE_MEDIUM_ATTRIBUTE) || 'inline';
        const id = input.id;
        const template = errorMedium === 'tooltip' ? this.errorMessageTooltipHTMLElement : this.errorMessageHTMLElement;

        if (!errorMessage) {
            console.error('klt/form-fields: Could not render error message async. No message given');
            return;
        }

        const wrapper = input.closest('[data-form-fields-target="fieldWrapper"]');

        if (wrapper) {
            const errorHTML = bindDataToTemplate({ errorMessage, id }, template);
            wrapper.insertAdjacentHTML('afterend', errorHTML);
            input.setAttribute('aria-invalid', 'true');
            input.setAttribute('aria-describedby', `${input.id}-error`);

            if (errorMedium === 'tooltip') {
                initTooltips();
            }
        }

        input.focus();
    }

    private removeError(input: HTMLInputElement) {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');

        const errorElement = document.getElementById(`${input.id}-error`);
        if (errorElement) {
            errorElement.remove();
        }
    }

    private getFieldsOfGroup(group: string) {
        return this.maskedFieldTargets.filter(
            (maskedField) =>
                maskedField.getAttribute(`data-${FormFieldsController.componentName}-group-param`) === group.toString()
        );
    }

    private getIndexOfFieldInGroup(groupFields: HTMLInputElement[], field: HTMLInputElement) {
        return groupFields.indexOf(field);
    }

    /**
     * Update the given counterElement with the character count of the given inputElement
     */
    private updateCharacterCounter(counterElement: HTMLElement, inputElement: HTMLInputElement | HTMLTextAreaElement) {
        if (inputElement.maxLength > 0) {
            counterElement.innerText = `${inputElement.value.length || 0}/${inputElement.maxLength}`;
        } else {
            counterElement.innerText = `${inputElement.value.length || 0}`;
        }
    }

    /**
     * @returns Returns true when state is valid, false when field is empty and therefore not valid
     */
    private validate(): boolean {
        let isValid = false;

        if (this.disabledWhenInvalidValue) {
            const submitButtons = this.element.querySelectorAll<HTMLButtonElement>('[type="submit"]');
            let disable = false;

            if (this.hasInputTarget) {
                this.inputTargets.forEach((input) => {
                    if (input.minLength) {
                        disable = disable || input?.value?.length < input.minLength;
                    }
                });
            }

            submitButtons.forEach((button) => (button.disabled = disable));

            isValid = !disable;
        } else {
            isValid = true;
        }

        return isValid;
    }
}
